import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import dayjs from 'dayjs';
import * as request from '../../Common/Util/Request';
import * as excel from '../../Common/Util/Excel';
import * as Button from '../../Common/Component/Button';
import Table from '../../Common/Component/Table/index';
import './index.scss';
import Swal from 'sweetalert2';
import { createTheme, ThemeProvider } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

function Sales(props) {
  const intl = useIntl();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  const loadPayments = useCallback(
    ({ page, limit }) => {
      const params = { page, limit };

      if (startDate) {
        params.start_date = moment(startDate).format('YYYY-MM-DD');
      }
      if (endDate) {
        params.end_date = moment(endDate).format('YYYY-MM-DD');
      }

      return request.payments(params).then(({ transactionGroup }) => transactionGroup);
    },
    [startDate, endDate],
  );

  const fetchData = useCallback(
    ({ pageIndex, pageSize }) => {
      loadPayments({ page: pageIndex + 1, limit: pageSize }).then((payments) => {
        setPayments(payments.rows);
        setTotal(payments.count);
      });
    },
    [loadPayments],
  );

  const columns = useMemo(
    () => [
      { Header: '일자', accessor: 'date_paid_at' },
      {
        Header: '요일',
        accessor: (row) => <FormattedMessage id={'ID_WEEKDAY_' + moment(row.date_paid_at).day()} />,
      },
      {
        Header: '결제건',
        accessor: 'count',
        Cell: ({ value }) => value.toPrice(),
      },
      {
        Header: '정가 합계',
        accessor: 'originalPriceSum',
        Cell: ({ value }) => value.toPrice(),
      },
      {
        Header: '할인금액 합계',
        accessor: 'discountPriceSum',
        Cell: ({ value }) => value.toPrice(),
      },
      {
        Header: '매출 합계',
        accessor: 'salesPriceSum',
        Cell: ({ value }) => value.toPrice(),
      },
      {
        Header: '온라인 매출',
        accessor: 'onlineSale',
        Cell: ({ value }) => value.toPrice(),
      },
      {
        Header: '오프라인 매출',
        accessor: 'offlineSale',
        Cell: ({ value }) => value.toPrice(),
      },
      {
        Header: '외국인 매출',
        accessor: 'foreignerSale',
        Cell: ({ value }) => value.toPrice(),
      },
      {
        Header: '환불금액',
        accessor: 'refundSum',
        Cell: ({ value }) => value.toPrice(),
      },
    ],
    [],
  );

  const [stat, setStat] = useState(null);

  const statRow = useMemo(
    () =>
      stat ? (
        <tr>
          <td>총합</td>
          <td>-</td>
          <td>{stat.transactionSum.toPrice()}</td>
          <td>{stat.originalPriceSum.toPrice()}</td>
          <td>{stat.discountPriceSum.toPrice()}</td>
          <td>{stat.salesPriceSum.toPrice()}</td>
          <td>{stat.onlineSale.toPrice()}</td>
          <td>{stat.offlineSale.toPrice()}</td>
          <td>{stat.foreignerSale.toPrice()}</td>
          <td>{stat.refundSum.toPrice()}</td>
        </tr>
      ) : null,
    [stat],
  );

  useEffect(() => {
    const params = {};
    if (startDate) {
      params.start_date = moment(startDate).format('YYYY-MM-DD');
    }
    if (endDate) {
      params.end_date = moment(endDate).format('YYYY-MM-DD');
    }

    request.paymentStat(params).then(setStat);
  }, [startDate, endDate]);

  const onClickCSV = useCallback(() => {
    const startDateMoment = startDate ? moment(startDate) : moment('2020-01-01');
    const endDateMoment = endDate ? moment(endDate) : moment();
    if (endDateMoment.diff(startDateMoment, 'days') > 365) {
      Swal.fire({
        title: '기간 설정 오류',
        text: '다운로드 가능한 기간은 최대 1년입니다. 기간을 다시 설정해주세요.',
        icon: 'error',
      });
      return;
    }

    setIsDownloading(true);
    loadPayments({ page: 1, limit: 9999 })
      .then((payments) => {
        excel.downloadXLSX(
          payments.rows.map((payment) => ({
            일자: payment.date_paid_at,
            요일: intl.formatMessage({
              id: 'ID_WEEKDAY_' + moment(payment.date_paid_at).day(),
            }),
            결제건: payment.count,
            '정가 합계': payment.originalPriceSum,
            '할인금액 합계': payment.discountPriceSum,
            '매출 합계': payment.salesPriceSum,
            '온라인 매출': payment.onlineSale,
            '오프라인 매출': payment.offlineSale,
            '외국인 매출': payment.foreignerSale,
            환불금액: payment.refundSum,
          })),
        );
        setIsDownloading(false);
      })
      .catch(() => {
        Swal.fire({
          icon: 'error',
          title: '다운로드 실패',
          text: '다운로드에 실패하였습니다. 다시 시도해주세요.',
        });
        setIsDownloading(false);
      });
  }, [intl, loadPayments, startDate, endDate]);

  const ejeTheme = createTheme({
    palette: {
      pink: {
        light: '#f46981',
        main: '#F24462',
        dark: '#a92f44',
        contrastText: '#fff',
      },
    },
  });

  return (
    <div className="sales">
      <ThemeProvider theme={ejeTheme}>
        <div className="sales__date">
          기간
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={startDate && dayjs(startDate)}
              maxDate={endDate && dayjs(endDate)}
              onChange={(value) => setStartDate(dayjs(value).format('YYYY-MM-DD'))}
              label={startDate && 'From'}
              disableFuture
              views={['year', 'month', 'day']}
            />
            -
            <DatePicker
              value={endDate && dayjs(endDate)}
              minDate={startDate && dayjs(startDate)}
              onChange={(value) => setEndDate(dayjs(value).format('YYYY-MM-DD'))}
              label={endDate && 'From'}
              disableFuture
              views={['year', 'month', 'day']}
            />
          </LocalizationProvider>
        </div>
        <div className="sales__total">
          총 매출건수 {total}
          <button
            className={'sales__excel--btn' + (!startDate && !endDate ? ' sales__excel__disabled--btn' : '')}
            style={{ cursor: isDownloading ? 'not-allowed' : 'pointer' }}
            onClick={() => {
              !startDate && !endDate
                ? Swal.fire({
                    title: '기간 설정 후 다운로드 가능합니다.',
                    text: '좌측 상단에서 기간 설정 후 다운로드해 주세요.',
                    icon: 'info',
                    confirmButtonColor: '#F24462',
                  })
                : !isDownloading
                ? onClickCSV()
                : undefined;
            }}
          >
            {isDownloading ? 'Loading...' : '액셀 다운로드'}
          </button>
        </div>
        <Table data={payments} columns={columns} total={total} pageSize={40} fetchData={fetchData} fixedRow={statRow} />
      </ThemeProvider>
    </div>
  );
}

export default Sales;
